<template>
  <v-container>
    <ValidationObserver
      ref="observer"
      v-slot="{ invalid }"
    >
      <v-row>
        <loading-overlay :is-loading="isLoading" />
        <template v-if="!(isEdit && withRailRod)">
          <v-divider class="my-10" />
          <v-col cols="12">
            <p>{{ $t(`enums.OrderConfigurator.${curtainType}.title`) }}</p>
            <v-radio-group v-model="withRailRod">
              <v-radio
                :label="$t(`enums.OrderConfigurator.${curtainType}.yes`)"
                :value="true"
              />
              <v-radio
                :label="$t(`enums.OrderConfigurator.${curtainType}.no`)"
                :value="false"
              />
            </v-radio-group>
          </v-col>
        </template>
        <v-col
          cols="12"
          class="py-0"
        >
          <v-divider class="0" />
        </v-col>
        <flow-generator
          v-if="hasCurtainTemplate"
          ref="flowGenerator"
          :schema="flowSchema"
          :value="flowData"
          class="flow"
        />
        <v-col
          v-else-if="!$wait.is('isLoadingCurtainTemplate')"
          cols="12"
        >
          <h3>
            {{ $t('components.curtainFlow.noTemplateAvailableText') }}
          </h3>
        </v-col>
        <v-expansion-panels
          v-if="withRailRod && isMainFlowValid"
          mandatory
          style="box-shadow: none"
        >
          <v-expansion-panel key="step-railRodLengthStep">
            <v-expansion-panel-header ref="rodWidthQuestion">
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <h4 class="text-h3 text-uppercase">
                    {{ $t(`components.curtainFlow.${railRod}-width`) }}
                  </h4>
                </v-col>
                <v-col
                  sm="6"
                  class="d-none d-sm-block"
                >
                  <h4 class="text-h3 text-uppercase">
                    Measuring instructions
                  </h4>
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-form
                ref="form"
                v-model="validForm"
                style="width: 100%"
              >
                <form-instruction>
                  <template slot="form">
                    <div style="max-width: 570px">
                      <v-img
                        class="mb-10"
                        :src="require('@/assets/images/bocht-geen-afm.jpg')"
                      />
                      <ValidationProvider
                        v-slot="{ errors }"
                        :rules="lengthRules"
                        name="length"
                      >
                        <p class="mb-5">
                          {{ lengthTitle }}
                        </p>
                        <v-text-field
                          v-model="railRodSize"
                          name="length"
                          outlined
                          dense
                          solo
                          flat
                          label="Centimeter"
                          :error-messages="errors"
                          @keydown.enter.prevent
                        >
                          <template slot="append">
                            Centimeter
                          </template>
                        </v-text-field>
                      </ValidationProvider>
                    </div>
                  </template>
                  <template slot="text">
                    <h4 class="text-h3 text-uppercase mb-5 hidden-sm-and-up">
                      How to measure
                    </h4>
                    <p class="mb-8">
                      Please use a metal tape measure that is long enough to
                      measure the width of the window in one go. Please measure
                      at least twice to avoid mistakes.
                    </p>
                    <p class="mb-8">
                      The curtain is calculated from the width of your track or rod. With an equal pair we have added an extra 5 cm to create extra fullness at the closure to avoid light coming through.
                    </p>
                    <p class="mb-0">
                      <strong>The length of a curtain {{ trackOfRod }} always
                        includes the end caps
                      </strong>
                    </p>
                    <p class="mb-8">
                      We always advise, if possible, to order a curtain
                      {{ trackOfRod }} that is wider than the window frame to
                      avoid the light coming through. A margin of 10 cm on each
                      side is ideal.
                    </p>
                    <p class="mb-0">
                      <strong>How to measure the width of a straight curtain
                        {{ trackOfRod }} between two walls?</strong>
                    </p>
                    <p class="mb-8">
                      Carefully measure the distance between the two walls and
                      deduct 1 cm. Now the {{ trackOfRod }} can be installed
                      easily.
                    </p>
                    <div>
                      <ValidationProvider
                        v-slot="{ errors }"
                        rules="required"
                        name="instructions"
                      >
                        <v-checkbox
                          v-model="checkbox"
                          value="1"
                          :error-messages="errors"
                          class="mb-8"
                          label="I have read the measuring instructions."
                          required
                        />
                      </ValidationProvider>
                    </div>
                    <p
                      v-if="checkbox && isEdit"
                      style="color: red; margin-bottom: 0;"
                    >
                      You have just edited your {{ trackOfRod }} size. Please don't forget to edit your curtain if required.
                    </p>
                  </template>
                </form-instruction>
              </v-form>

              <v-divider />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-row>
      <v-row
        v-if="hasCurtainTemplate"
        class="pl-3 pr-3"
      >
        <v-col
          cols="12"
          md="6"
        >
          <fabric-picker-card />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <div class="confirmButtonsBlock">
            <v-btn
              class="primary opacity-bg"
              @click="$emit('previous')"
            >
              <template v-if="isEdit">
                Edit curtain
              </template>
              <template v-else>
                <v-icon class="white--text pr-5">
                  mdi mdi-chevron-left
                </v-icon>
                {{ $t('Previous') }}
              </template>
            </v-btn>

            <save-edit-button
              v-if="isEdit"
              :disabled="invalid || withRailRod === null"
              :loading="
                $wait.is('isLoadingCurtainPrice') ||
                  $wait.is('isArticleUpdating')
              "
              :save-button-state="saveButtonState"
              @next="next"
            />
            <v-btn
              v-else
              class="primary"
              :disabled="invalid || withRailRod === null"
              :loading="
                $wait.is('isLoadingCurtainPrice') ||
                  $wait.is('isArticleUpdating')
              "
              @click="next"
            >
              <template>
                {{ $t('Next') }}
                <v-icon class="white--text pl-5">
                  mdi mdi-chevron-right
                </v-icon>
              </template>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { _ } from 'vue-underscore'
import FlowGenerator from 'vue-flow-generator/src/components/FlowGenerator/FlowGenerator'
import { FlowGeneratorBus } from 'vue-flow-generator/src/plugins/bus'
import CurtainFlow from '@/mixins/Flows/CurtainsRods'
import { TRACK_RAIL, SPOTLIGHT_ROD } from '@/constants.js'
const FabricPickerCard = () => import('@/components/Cards/FabricPickerCard')
import FormInstruction from '@/components/FlowComponents/FormInstruction'
import LoadingOverlay from '@/components/Overlays/LoadingOverlay'
import { mapActions, mapGetters } from 'vuex'
import { FlowGeneratorMixin } from '@/mixins/Flows/utils'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import FlowGeneratorActions from '../../mixins/FlowGeneratorActions'
import { EditFlowComponentMixin } from '@/mixins/EditFlow'
import SaveEditButton from '@/components/Buttons/SaveEditButton'

export default {
  name: 'CurtainFlow',
  components: {
    FlowGenerator,
    LoadingOverlay,
    FabricPickerCard,
    FormInstruction,
    ValidationObserver,
    ValidationProvider,
    SaveEditButton
  },
  mixins: [FlowGeneratorMixin, FlowGeneratorActions, EditFlowComponentMixin],
    props: {
    saveButtonState: {
      type: String,
      default: "default"
    },
  },
  data() {
    return {
      flowData: {},
      validForm: false,
      checkbox: null
    }
  },
  computed: {
    ...mapGetters({
      curtainParameters: 'order/getCurtainParameters',
      curtainTemplate: 'order/getCurtainTemplate',
      curtainPriceData: 'order/getCurtainPriceData',
      curtainType: 'order/getCurtainType',
      mainFabric: 'order/getFabric',
      railRod: 'order/getRailRodType',
      railRodParameters: 'order/getRailRodParameters',
      resetKey: 'order/getResetKey'
    }),
    trackOfRod() {
      return this.curtainType === 'ringcurtain' ? 'rod' : 'track'
    },
    flowSchema() {
      return this.withRailRod !== null
        ? this.getFlowSchema()
        : {}
    },
    hasCurtainTemplate() {
      return !_.isEmpty(this.curtainTemplate)
    },
    lengthTitle() {
      return this.curtainType === 'ringcurtain'
        ? 'What is the required rod size? (A)'
        : 'What is the required track size? (A)'
    },
    isLoading() {
      return (
        this.$wait.is('isLoadingCurtainTemplate') ||
        this.$wait.is('isLoadingRailRodTemplate')
      )
    },
    lengthRules() {
      let splitType = this.curtainParameters.split_type
      let max_value = splitType === 'left' || splitType === 'right' ? 200 : 360
      return this.trackOfRod === 'rod'
        ? `required|double:1|min_value:132|max_value:${max_value}`
        : `required|double:1|min_value:20|max_value:600`
    },
    withRailRod: {
      async set(data) {
        await this.$store.dispatch('order/setWithRailRod', data)
      },
      get() {
        return this.$store.getters['order/getWithRailRod']
      }
    },
    railRodSize: {
      set(val) {
        this.addRailRodParameter({
          width: val,
          length: val
        })
      },
      get() {
        return this.railRodParameters['width'] || null
      }
    },
    isMainFlowValid() {
      return this.curtainParameters && this.isFlowValid()
    }
  },
  watch: {
    resetKey() {
      this.$refs.observer.reset()
      this.checkbox = null
    },
    mainFabric: function (fabric) {
      this.addCurtainParameter({ main_fabric: fabric.fabric_uuid })
    },
    curtainType: function () {
      this.cleanData()
      this.$store.dispatch('order/setCurtainParameters', {
        main_fabric: this.mainFabric.fabric_uuid
      })
    },
    async isMainFlowValid(val) {
      if (val && this.$refs.rodWidthQuestion) {
        await this.$nextTick()
        await this.$vuetify.goTo(this.$refs.rodWidthQuestion)
      }
    }
  },
  mounted() {
    FlowGeneratorBus.$on(
      'valid-flow-field-input',
      ({ flowName, id, model }) => {
        if (flowName === 'CurtainFlow') {
          if (['support_type', 'color'].includes(id)) {
            this.addRailRodParameter({ [id]: model })
            if (id === 'support_type') {
              // this.addRailRodParameter({ 'support_size': '35mmsize' })
            }
          } else {
            this.addCurtainParameter({ [id]: model })
          }
        }
      }
    )
  },
  methods: {
    ...mapActions({
      addCurtainParameter: 'order/addCurtainParameter',
      addRailRodParameter: 'order/addRailRodParameter'
    }),
    next() {
      this.$emit('next')
      if (
        this.railRodSize &&
        !this.curtainParameters.width &&
        this.withRailRod
      ) {
        const add = this.railRod === SPOTLIGHT_ROD ? 5 : 0
        this.addCurtainParameter({ width: Number(this.railRodSize) + add })
      }
    },
    getFlowSchema() {
      return CurtainFlow(
        this.curtainTemplate,
        this.curtainParameters,
        this.curtainType,
        this.mainFabric,
        this.railRod,
        this.withRailRod,
        this.railRodParameters.color // color support elements for track
      )
    }
  }
}
</script>

<style scoped lang="scss">
.v-text-field .v-input__append-inner {
  color: gray;
}
</style>
